import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { CreateProjectDialog, UpdateProjectDialog } from './components';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectProjects, selectProjectsIsEmpty, selectProjectsLoading } from 'store/features/projects/selector';

const Projects = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const loading = useAppSelector(selectProjectsLoading);
  const isEmptyProjects = useAppSelector(selectProjectsIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [openDeleteProject, setOpenDeleteProject] = useState(false);
  const [openCreateProject, setOpenCreateProject] = useState(false);
  const [openUpdateProject, setOpenUpdateProject] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projects.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
      {
        field: 'groupTitle',
        headerName: intl.formatMessage({ id: 'projects.groupTitle', defaultMessage: 'Grup Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(projectsActions.getProjects());
  };

  // sync helper functions
  const handleCloseDeleteProject = () => setOpenDeleteProject(false);
  const handleClickOpenDeleteProject = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteProject(true);
  };

  const handleCloseCreateProject = () => setOpenCreateProject(false);
  const handleClickOpenCreateProject = () => setOpenCreateProject(true);

  const handleCloseUpdateProject = () => setOpenUpdateProject(false);
  const handleClickOpenUpdateProject = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateProject(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-product')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!projects.length && !isEmptyProjects) {
      dispatch(projectsActions.getProjects());
    }
  }, [projects, isEmptyProjects, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-product']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateProject}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="projects.createProject" defaultMessage="Ürün Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-product']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateProject}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="projects.updateProject" defaultMessage="Ürün Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-product']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteProject}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="projects.deleteProject" defaultMessage="Ürün Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateProjectDialog open={openCreateProject} handleClose={handleCloseCreateProject} refresh={refresh} />
      <UpdateProjectDialog
        open={openUpdateProject}
        projectId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateProject}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteProject}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteProject}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="projects.deleteProject" defaultMessage="Ürün Sil" />}
        subTitle={
          <FormattedMessage
            id="gibTypes.deleteGibTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra GİB Yöntemi veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteProject}
        successMessage={
          <FormattedMessage
            id="projects.deleteProjectRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra ürün veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={projects}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Projects;
